import { AbstractControl, AsyncValidatorFn, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Observable, combineLatestWith, map, of, tap } from 'rxjs';
import { NBPConfig } from '@bo-client/shared/config.model';
import { take } from 'rxjs/operators';

/**
 * Validates if the total power of the components (producers and PV modules)
 * does not exceed the requested power values.
 */
export class CustomValidators {
  /**
   * Validates the control's value asynchronously.
   * @returns An Observable that emits a ValidationErrors object if the validation fails, or null if it passes.
   * @param tenantConfig$
   */
  static createPVModulePowerValidator(tenantConfig$: Observable<NBPConfig>): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      // This should be applied to the formgroup
      const newInstalledPower = control?.get('newInstalledPower').value;
      const newProduction = control?.get('newProduction').value;

      return control.valueChanges.pipe(
        combineLatestWith(tenantConfig$),
        map(([formValue, tenantConfig]) => {
          return {
            value: formValue,
            producerPowerTotal: formValue?.producers
              ?.map((x) => x.acApparentPowerMax * x.count)
              .reduce((a, b) => a + b, 0),
            modulePowerTotal: formValue?.pvModules
              ?.map((x) => x.count * (x.modulePowerWatt / 1000))
              .reduce((a, b) => a + b, 0),
            threshold: tenantConfig.gridRegistration.allowedPowerDeviationPercent
          };
        }),
        map(({ value, threshold, producerPowerTotal, modulePowerTotal }) => {
          const effectivePowerNetto = Math.min(producerPowerTotal, modulePowerTotal);
          /**
           */
          const brokenEEgThreshold = newInstalledPower < 25 && modulePowerTotal >= 25;
          const brokenEEGThreshold2 = newInstalledPower < 100 && modulePowerTotal >= 100;

          if (
            effectivePowerNetto > newInstalledPower + (newInstalledPower / 100) * (threshold || 0.01) ||
            effectivePowerNetto > newProduction + (newProduction / 100) * (threshold || 0.01) ||
            brokenEEgThreshold ||
            brokenEEGThreshold2
          ) {
            return { thresholdExceeded: true };
          }
          return null;
        })
      );
    };
  }

  /**
   * **Synchroner** Validator (empfängt `tenantConfig` als Wert, nicht als Observable).
   */
  static createPVModulePowerValidatorSync(allowedPowerDeviationPercent?: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // Hole die Werte synchron vom Control
      const newInstalledPower = control?.get('newInstalledPower')?.value || 0;
      const newProduction = control?.get('newProduction')?.value || 0;

      // "Rohdaten" der Form
      const formValue = control.value || {};
      const producers = formValue.producers ?? [];
      const pvModules = formValue.pvModules ?? [];

      const threshold = allowedPowerDeviationPercent || 0.01;

      // Gesamtleistung errechnen
      const producerPowerTotal = producers
        .map((x: any) => x.acApparentPowerMax * x.count)
        .reduce((a: number, b: number) => a + b, 0);

      const modulePowerTotal = pvModules
        .map((x: any) => x.count * (x.modulePowerWatt / 1000))
        .reduce((a: number, b: number) => a + b, 0);

      const effectivePowerNetto = Math.min(producerPowerTotal, modulePowerTotal);

      // Beispiel-Checks
      const brokenEEgThreshold = newInstalledPower < 25 && modulePowerTotal >= 25;
      const brokenEEgThreshold2 = newInstalledPower < 100 && modulePowerTotal >= 100;

      if (
        effectivePowerNetto > newInstalledPower + (newInstalledPower / 100) * threshold ||
        effectivePowerNetto > newProduction + (newProduction / 100) * threshold ||
        brokenEEgThreshold ||
        brokenEEgThreshold2
      ) {
        return { thresholdExceeded: true };
      }
      return null;
    };
  }
}
